import React, { useState } from 'react';
import { useSpring, animated } from '@react-spring/web';
import { useTheme } from 'styled-components';
import useBreakpoint from 'Common/hooks/useBreakpoint';

import Image, { StaticImageData } from 'next/image';
import { LinkProps } from 'Elements/Link';

import {
    Container,
    Content,
    ImageWrapper,
    Heading,
    Quote,
    AvatarWrapper,
    Title,
    Organisation,
    StyledLink,
    Featured,
    FeaturedText,
    ReadTab,
    ContentWrapper,
    AnimatedImageWrapper,
    Subject,
    Wrapper
} from './SolentStory.styled';

export interface BackgroundColor {
    primary: string;
    secondary: string;
}

export interface Props {
    id?: string;
    featured: boolean;
    videoStory?: boolean;
    heading: string;
    subject?: string;
    headingType?: 'h2' | 'h3';
    title?: string;
    organisation?: string;
    minutes?: number;
    quote?: string;
    link: LinkProps;
    themeColor: BackgroundColor;
    images: {
        avatar: [
            {
                src: StaticImageData | string;
                alt: string;
            },
            {
                src: StaticImageData | string;
                alt: string;
            }
        ];
        featureImage: {
            src: StaticImageData | string;
            alt?: string;
        };
    };
}

const SolentStory: React.FC<Props> = props => {
    const [hovered, setIsHovered] = useState(false);
    const { breakpoints } = useTheme();
    const isMediumBreakpoint = useBreakpoint(breakpoints.medium.value);

    const {
        images,
        heading,
        headingType = 'h2',
        link,
        quote,
        title,
        organisation,
        featured,
        minutes,
        themeColor,
        subject,
        videoStory
    } = props;

    const springconfig = {
        friction: 30,
        tension: 300
    };

    const slideOut = useSpring({
        opacity: hovered && isMediumBreakpoint ? 0 : 1,
        left: hovered && isMediumBreakpoint ? '-20%' : '0%',
        config: springconfig
    });

    const slideIn = useSpring({
        opacity: hovered && isMediumBreakpoint ? 1 : 0,
        left: hovered && isMediumBreakpoint ? '0%' : '20%',
        config: springconfig
    });

    return (
        <Wrapper>
            {!featured && <Subject>{subject}</Subject>}
            <Container
                $featured={featured}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                {featured && (
                    <Featured $hovered={hovered}>
                        <FeaturedText>Featured</FeaturedText>
                    </Featured>
                )}
                <ImageWrapper $featured={featured}>
                    <AnimatedImageWrapper
                        $featured={featured}
                        $hovered={hovered && !featured}
                        $blur={hovered}
                    >
                        <Image
                            src={images.featureImage.src}
                            alt={images.featureImage.alt || ''}
                            fill
                            sizes="(max-width: 760px) 100vw"
                            style={{ objectFit: 'cover' }}
                        />
                    </AnimatedImageWrapper>
                </ImageWrapper>
                {featured && quote && <Quote $hovered={hovered}>“{quote}”</Quote>}
                <StyledLink {...link} data-hover={hovered} />
                <ContentWrapper>
                    <AvatarWrapper
                        $hovered={hovered}
                        $backgroundColor={themeColor}
                        $featured={featured}
                    >
                        <animated.div
                            style={{
                                position: 'absolute',
                                height: '100%',
                                width: '100%',
                                ...slideOut
                            }}
                        >
                            <Image
                                src={images.avatar[0].src}
                                alt={images.avatar[0].alt}
                                height={120}
                                width={120}
                                style={{ maxWidth: '100%', height: 'auto' }}
                            />
                        </animated.div>
                        <animated.div
                            style={{
                                position: 'absolute',
                                height: '100%',
                                width: '100%',
                                ...slideIn
                            }}
                        >
                            <Image
                                src={images.avatar[1].src}
                                alt={images.avatar[1].alt}
                                height={120}
                                width={120}
                                style={{ maxWidth: '100%', height: 'auto' }}
                            />
                        </animated.div>
                    </AvatarWrapper>
                    <Content $hovered={hovered} $backgroundColor={themeColor} $featured={featured}>
                        {heading && <Heading as={headingType}>{heading}</Heading>}
                        {title && <Title>{title}</Title>}
                        {organisation && <Organisation>{organisation}</Organisation>}
                    </Content>
                    {minutes && (
                        <ReadTab $hovered={hovered}>
                            {minutes} min {videoStory ? 'watch' : 'read'}
                        </ReadTab>
                    )}
                </ContentWrapper>
            </Container>
        </Wrapper>
    );
};

export default SolentStory;
